// Import Packages
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSetRecoilState } from 'recoil';
import RichTextEditor from "react-rte";

// Import Material UI
import DialogContent from "@material-ui/core/DialogContent";
import Grid from "@material-ui/core/Grid";
import { styled } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import DeleteIcon from '@material-ui/icons/Delete';
import { Close } from "@material-ui/icons";
import { makeStyles } from "@material-ui/styles";
import { Dialog, DialogActions, DialogTitle, TextField } from "@material-ui/core";

// Import Pages
import { alertState } from "../../../../recoil-store/recoil-state";
import LibraryService from "../../../../services/LibraryService/LibraryService";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import MateService from "../../../../services/MateService/MateService";

// Import Services
import ApplicationService from "../../../../services/ApplicationService/ApplicationService";
import ModuleService from "../../../../services/ModuleService/ModuleService";

// Import CSS Files
import "./NewLibrary.css";
import { getExtension } from "../../../../helpers/helper";


const Input = styled('input')({
    display: 'none',
});
const useStyles = makeStyles((theme) => ({
    input: {
        backgroundColor: "#FCFCFC",
        boxSizing: "border-box",
        borderRadius: "2px",
        marginBottom: "10px"
    },
}));
const StepSix = (props) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const fileInputRef = useRef(null);
    const jsonInputRef = useRef(null);
    const [libraryName, setLibraryName] = useState("");
    const [fileName, setFileName] = useState([]);
    const [libraryImg, setLibraryImg] = useState();
    const [previewUrl, setPreviewUrl] = useState(null);
    const [jsonName, setJsonName] = useState();
    const [editorState, setEditorState] = useState(RichTextEditor.createEmptyValue())
    const [applications, setApplications] = useState([]);
    const [modules, setModules] = useState([]);
    const [selectedApplication, setSelectedApplication] = useState("");
    const [selectedLibraryModule, setSelectedLibraryModule] = useState("");
    const setAlert = useSetRecoilState(alertState);

    useEffect(() => {
        getApplications().then(r => r);
        getModules().then(r => r);
    }, []);

    const getApplications = async () => {
        const response = await ApplicationService.getApplications();
        if (response.status) {
            setApplications(response.data);
        }
    };

    const getModules = async () => {
        const response = await ModuleService.getModules();
        if (response.status) {
            setModules(response.data);
        }
    };

    const handleEdit = value => {
        setEditorState(value)
    }


    const handleClose = () => {
        props.setOpen(false);
    }


    const uploadLibraryFile = (event) => {
        if (event.target.files[0]?.name) {
            let extension = getExtension(event.target.files[0]?.name);
            if (extension === 'jpeg' || extension === 'png' || extension === 'svg') {
                const file = event.target.files[0];
                
                const reader = new FileReader();
                reader.onload = (e) => {
                    const img = new Image();
                    img.src = e.target.result;

                    img.onload = () => {
                        if (img.width === 40 && img.height === 40) {
                            setPreviewUrl(e.target.result);
                            setLibraryImg(file);
                        } else {
                            setAlert(alert => ({
                                ...alert,
                                show: true,
                                message: t('MESSAGES.PUBLISH.IMAGE-INCOMPATIBLE-PIXELS'),
                                severity: 'error'
                            }))
                        }
                    };                    
                };
                reader.readAsDataURL(file);
            } else {
                setAlert(alert => ({
                    ...alert,
                    show: true,
                    message: t('MESSAGES.PUBLISH.IMAGE-FORMAT-ERROR'),
                    severity: 'error'
                }))
            }
        }
    }

    const handleInputChange = (event) => {
        if (event.target.files[0]?.name) {
            let extension = getExtension(event.target.files[0]?.name);
            if (extension === 'json') {
                setJsonName(event.target.files[0]);
            } else {
                setAlert(alert => ({
                    ...alert,
                    show: true,
                    message: t('MESSAGES.PUBLISH.JSON-FORMAT-ERROR'),
                    severity: 'error'
                }))
            }
        }
    };

    const handleSave = async () => {
        if (!libraryName) {
            setAlert(alert => ({
                ...alert,
                show: true,
                message: t('MESSAGES.LIBRARY.LIBRARY-NAME-EMPTY'),
                severity: 'error'
            }))
            return
        }
        if (!selectedApplication) {
            setAlert(alert => ({
                ...alert,
                show: true,
                message: t('MESSAGES.LIBRARY.LIBRARY-APPLICATION-EMPTY'),
                severity: 'error'
            }))
            return
        }
        if (!selectedLibraryModule) {
            setAlert(alert => ({
                ...alert,
                show: true,
                message: t('MESSAGES.LIBRARY.LIBRARY-MODULE-EMPTY'),
                severity: 'error'
            }))
            return
        }
        const data = {
            library_name: libraryName,
            library_description: editorState.toString('html'),
            application_id: selectedApplication ? selectedApplication : null,
            image: fileName[0],
            jsonName: jsonName
        }

        let formData = new FormData();
        formData.append('files', jsonName);
        formData.append('files', libraryImg);
        formData.append('library_name', libraryName);
        formData.append('library_description', editorState.toString('html'));
        formData.append('application_id', selectedApplication ? selectedApplication : null);
        formData.append('module_id', selectedLibraryModule ? selectedLibraryModule : null);
        // formData.append('image', fileName[0]); //

        const { status, message } = await LibraryService.addLibrary(formData)
        if (status) {
            //success
            props.setRefresh(old => !old);
            setAlert(alert => ({
                ...alert,
                show: true,
                message: t('CONTENT.LIBRARIES.SUCCESSFULLY-LIBRARY'),
                severity: 'success'
            }))
            handleClose();
        } else {
            setAlert(alert => ({
                ...alert,
                show: true,
                message: t('MESSAGES.LIBRARY.' + message),
                severity: 'error'
            }))
        }
    }

    return (
        <Dialog
            id="publish-dialog"
            data-test="autom-add-new"
            open={true}
            onClose={handleClose}
            PaperProps={{
                style: {
                    padding: "32px 48px",
                    overflowX: "hidden"
                },
            }}
        >
            <DialogTitle
                style={{ padding: 0 }}
                id="alert-dialog-title"
            >
                <div id="about-title">
                    <span />
                    <Button data-test="cancelautom-button" onClick={handleClose}>
                        <Close />
                    </Button>
                </div>
            </DialogTitle>
            <DialogContent style={{ padding: 0 }}>
                <div style={{ textAlign: "left" }}>
                    <Grid container>
                        <Grid item xs={3}>
                            <span className="dialog-text">{t('CONTENT.LIBRARIES.LIBRARY-NAME')}<span className="mandatory-field">*</span> </span>
                        </Grid>
                        <Grid item xs={12} style={{ marginBottom: "1rem" }}>
                            <TextField
                                id="filled-size-small"
                                required
                                className={classes.input}
                                variant="outlined"
                                size="small"
                                fullWidth
                                inputProps={{ style: { fontSize: 12 } }}
                                value={libraryName}
                                onChange={e => setLibraryName(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <span style={{
                                fontSize: 12,
                                color: "#8F94A6",
                                fontWeight: 700
                            }}>{t('CONTENT.LIBRARIES.APPLICATION')}<span className="mandatory-field">*</span> </span>
                        </Grid>
                        <Grid item xs={12} style={{ marginBottom: "1rem" }}>
                            <FormControl
                                variant="outlined"
                                className={classes.formControl}
                                style={{ width: "100%" }}
                            >
                                <Select
                                    labelId="demo-simple-select-outlined-label"
                                    style={{
                                        backgroundColor: "#fff",
                                        height: "40px",
                                        fontSize: "12px",
                                    }}
                                    id="demo-simple-select-outlined"
                                    value={selectedApplication}
                                    onChange={(e) => setSelectedApplication(e.target.value)}
                                >
                                    {applications.length > 0 &&
                                        applications.map((app) => (
                                            <MenuItem
                                                key={app.id}
                                                style={{ fontSize: 12 }}
                                                value={app.id}
                                            >
                                                {app.name}
                                            </MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={3}>
                            <span style={{
                                fontSize: 12,
                                color: "#8F94A6",
                                fontWeight: 700
                            }}>{t('CONTENT.GENERAL.LIBRARY-MODULE')}<span className="mandatory-field">*</span> </span>
                        </Grid>
                        <Grid item xs={12} style={{ marginBottom: "1rem" }}>
                            <FormControl
                                variant="outlined"
                                className={classes.formControl}
                                style={{ width: "100%" }}
                            >
                                <Select
                                    labelId="demo-simple-select-outlined-label"
                                    style={{
                                        backgroundColor: "#fff",
                                        height: "40px",
                                        fontSize: "12px",
                                    }}
                                    id="demo-simple-select-outlined"
                                    value={selectedLibraryModule}
                                    onChange={(e) => setSelectedLibraryModule(e.target.value)}
                                >
                                    {modules.length > 0 &&
                                        modules.map((module) => (
                                            <MenuItem
                                                key={module.id}
                                                style={{ fontSize: 12 }}
                                                value={module.id}
                                            >
                                                {t("CONTENT.MODULE." + module.module_name)}
                                            </MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={3}>
                            <span style={{
                                fontSize: 12,
                                color: "#8F94A6",
                                fontWeight: 700
                            }}>{t('CONTENT.LIBRARIES.SHORT-DESC')}</span>
                        </Grid>
                        <Grid item xs={12}>
                            <RichTextEditor
                                editorClassName="publish-editor"
                                value={editorState}
                                onChange={value => handleEdit(value)}
                            />
                        </Grid>
                        <Grid item xs={6}
                            style={{ marginTop: "1rem" }}>
                            <div id="upload-box">
                                <label htmlFor="contained-button-file">
                                    <Input accept="image/png, image/jpeg, image/svg+xml" id="contained-button-file"
                                        type="file"
                                        onChange={e => uploadLibraryFile(e)} ref={fileInputRef} />
                                    <Button
                                        variant="outlined"
                                        component="span"
                                        style={{
                                            backgroundColor: "#46474E",
                                            borderRadius: "2px",
                                            color: "#fff",
                                            border: "none",
                                            fontWeight: 700
                                        }}
                                    >
                                        {t("CONTENT.PUBLISH.ADD-IMAGE")}<span className="mandatory-field">*</span>
                                    </Button>
                                    <small className="upload-help-text">{t("CONTENT.PUBLISH.ADD-IMAGE-HELP")}</small>
                                </label>
                            </div>
                        </Grid>
                        <Grid item xs={6}
                            style={{ marginTop: "1rem", display: "flex", justifyContent: "flex-end" }}>
                            <div id="upload-json-box">
                                <label htmlFor="contained-json-button-file">
                                    <input accept=".json" id="contained-json-button-file" className="custom-file-input"
                                        type="file"
                                        onChange={e => handleInputChange(e)} ref={jsonInputRef} />
                                    <Button
                                        variant="outlined"
                                        component="span"
                                        style={{
                                            backgroundColor: "#46474E",
                                            borderRadius: "2px",
                                            color: "#fff",
                                            border: "none",
                                            fontWeight: 700
                                        }}
                                    >
                                        {t("CONTENT.PUBLISH.UPLOAD-APP-TRIGGER-JSON")}
                                    </Button>
                                    <small
                                        className="upload-help-text">{t("CONTENT.PUBLISH.UPLOAD-APP-JSON-TRIGGER-TITLE")}</small>
                                </label>
                            </div>
                        </Grid>
                        <div style={{ display: "flex", justifyContent: "space-between", width: "100%", marginTop: 20 }}>
                            <Grid item xs={3}>
                                {previewUrl &&
                                    <ImageThumb file={previewUrl} setLibraryImg={setLibraryImg}
                                        fileInputRef={fileInputRef} setPreviewUrl={setPreviewUrl} />
                                }
                            </Grid>
                            <Grid item xs={3}>

                            </Grid>
                            {jsonName &&
                                <Grid key="json-file" item xs={6}
                                    style={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                                    <JsonFile index="json-file" file={jsonName} jsonName={jsonName}
                                        setJsonName={setJsonName}
                                        jsonInputRef={jsonInputRef} />
                                </Grid>
                            }
                        </div>
                    </Grid>
                </div>
            </DialogContent>
            <DialogActions style={{ padding: 0, justifyContent: "space-between", marginTop: 50 }}>
                <button className="cancel-button" onClick={handleClose} style={{
                    backgroundColor: "#46474E",
                    borderRadius: "2px",
                    color: "#fff",
                    width: "20%",
                    border: "none",
                    fontWeight: 700,
                    padding: "5px 15px"
                }}>{t('CONTENT.LIBRARIES.CANCEL')}</button>
                <button className="button-save" onClick={handleSave} style={{
                    backgroundColor: "#46474E",
                    borderRadius: "2px",
                    color: "#fff",
                    width: "20%",
                    border: "none",
                    fontWeight: 700,
                    padding: "5px 15px"
                }}>{t('CONTENT.LIBRARIES.SAVE')}</button>

            </DialogActions>
        </Dialog>

    );
};

const ImageThumb = ({ setLibraryImg, setPreviewUrl, file, fileInputRef }) => {
    const deleteImg = (e) => {
        fileInputRef.current.value = null;
        //setFileName(oldArr => oldArr.filter((item, indx) => indx !== Number(e.target.id)))
        setLibraryImg(null);
        setPreviewUrl(null);
    }

    return (
        <div className="attached-image-section">
            <img
                width="40px"
                height="40px"
                className="publish-clear-image"
                src={file}
                alt="publish-icon" />
            <span className="library-clear-icon" onClick={(e) => deleteImg(e)}>
                <DeleteIcon />
            </span>
        </div>
    );
};

const JsonFile = ({ jsonInputRef, jsonName, setJsonName }) => {
    const { t } = useTranslation();

    const deleteJson = (e) => {
        jsonInputRef.current.value = null;
        setJsonName(e.target.id);
    }

    return (
        <div className="custom-file">
            <span>{jsonName?.name ? jsonName.name : t('CONTENT.SETTINGS.GENERAL.CHOOSE')}</span>
            <span className="library-clear-icon" onClick={(e) => deleteJson(e)}>
                <DeleteIcon />
            </span>
        </div>
    )
}

export default StepSix;
